import { WayPointTarget } from "@/components/r3f/renderers/odometry-paths/walk-paths-renderer";
import { WaypointPosition } from "@/components/r3f/renderers/waypoint-label-render";
import { useCached3DObjectIfExists } from "@/object-cache";
import {
  isGaussianSplatsObject,
  isPanoObject,
  isPhotoObject,
  isSheetObject,
} from "@/object-cache-type-guard";
import { selectAll3DMeasurements } from "@/store/measurement-tool-selector";
import { useAppSelector } from "@/store/store-hooks";
import { ToolName } from "@/store/ui/ui-slice";
import { ActiveToolAndPicking, PickingToolsRef } from "@/tools/picking-tools";
import { assert } from "@faro-lotv/foundation";
import {
  GUID,
  IElementGenericAnnotation,
  IElementGenericImgSheet,
  IElementImageCommand,
  IElementImg360,
  isIElementImg2d,
  isIElementModel3dStream,
} from "@faro-lotv/ielement-types";
import { CachedWorldTransform } from "@faro-lotv/project-source";
import { type JSX } from "react";
import { shallowEqual } from "react-redux";
import { Box3, Plane, Vector3 } from "three";
import { WalkModelsStaticScene } from "./static/walk-models-static-scene";
import { WalkPanorama } from "./walk-panorama";
import {
  ViewType,
  WalkSceneActiveElement,
  WalkSceneOverlayElement,
} from "./walk-types";

type WalkSceneStaticProps = PickingToolsRef & {
  /** The main element of the scene to render */
  currentElement: WalkSceneActiveElement;

  /** Optional overlay element, to enable e.g. CAD/cloud overlay */
  overlayElement?: WalkSceneOverlayElement;

  /** The 2D photo object (image command) to look at */
  photoObject?: IElementImageCommand;

  /** Element that should be highlighted, e.g. with a hover effect */
  highlightedElement?: GUID;

  /** The floor image where the placeholders are placed */
  sheetForElevation?: IElementGenericImgSheet;

  /** Which view is this: main view in walk mode, left view, or right view in splitscreen mode */
  viewType: ViewType;

  /** List of all the placeholders to show */
  placeholders: IElementImg360[];

  /** If true other panorama's placeholders will be rendered  */
  shouldShowPlaceholders: boolean;

  /** List of the annotations for this area */
  annotations: IElementGenericAnnotation[];

  /** Describe the active tool, and whether this tool is using picking */
  activeToolAndPicking: ActiveToolAndPicking;

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /** Whether the 3d models should be rendered on demand */
  renderOnDemand: boolean;

  /** Callback executed when walks to a new position in the scene */
  onUserWalkedTo(p: Vector3): void;

  /** Callback executed when the active element should be changed */
  onTargetElementChanged(element: WalkSceneActiveElement): void;

  /** Callback called when the active waypoint target element inside the walk scene has changed. */
  onWayPointChanged(target: WayPointTarget): void;

  /** Callback called, when the currently rendered pano changed its position. */
  onPanoTransformChanged(pose: CachedWorldTransform): void;

  /** Callback called when the list of displayed waypoints has changed. */
  onRenderedWaypointsChanged(newWaypoints: WaypointPosition[]): void;

  /** Optional callback to signal outside that the bounding box of all models has changed. */
  onAllModelsBoxChanged?(box: Box3): void;
};

/** @returns A static scene for the walk mode */
export function WalkSceneStatic({
  currentElement,
  overlayElement,
  photoObject,
  highlightedElement,
  viewType,
  placeholders,
  shouldShowPlaceholders: shouldShowPlaceholdersProp,
  sheetForElevation,
  activeToolAndPicking,
  annotations,
  clippingPlanes,
  renderOnDemand,
  onUserWalkedTo,
  onTargetElementChanged,
  onWayPointChanged,
  onRenderedWaypointsChanged,
  onAllModelsBoxChanged,
  onPanoTransformChanged,
  ...toolEvents
}: WalkSceneStaticProps): JSX.Element | null {
  /** Get the current 3D object (do not report loading failure for the IElementModel3dStream)*/
  const displayLoadingErrorCurrentElement =
    !isIElementModel3dStream(currentElement) ||
    !isIElementImg2d(currentElement);
  const currentObject = useCached3DObjectIfExists(
    currentElement,
    "WalkSceneStatic",
    displayLoadingErrorCurrentElement,
  );
  const displayLoadingErrorOverlayElement =
    !!overlayElement && !isIElementModel3dStream(overlayElement);
  const overlayObject = useCached3DObjectIfExists(
    overlayElement,
    "WalkSceneStatic",
    displayLoadingErrorOverlayElement,
  );

  const measurements = useAppSelector(selectAll3DMeasurements, shallowEqual);

  const shouldShowPlaceholders =
    shouldShowPlaceholdersProp &&
    (!activeToolAndPicking.isPickingToolActive ||
      activeToolAndPicking.activeTool === ToolName.measurement);
  const shouldShowAnnotations = !activeToolAndPicking.isPickingToolActive;

  if (isPhotoObject(currentObject) || isSheetObject(currentObject)) {
    return null;
  }

  // Split mode (the only consumer of this scene) already rejects Gaussian Splats before reaching here.
  assert(
    !isGaussianSplatsObject(currentObject),
    "WalkSceneStatic does not support Gaussian Splats",
  );

  if (isPanoObject(currentObject)) {
    return (
      <WalkPanorama
        placeholders={placeholders}
        shouldShowPlaceholders={shouldShowPlaceholders}
        shouldShowAnnotations={shouldShowAnnotations}
        highlightedElement={highlightedElement}
        isPickingToolActive={activeToolAndPicking.isPickingToolActive}
        pano={currentObject}
        photoObject={photoObject}
        annotations={annotations}
        measurements={measurements}
        sheetForElevation={sheetForElevation}
        visible
        clippingPlanes={clippingPlanes}
        isSecondaryView={viewType === ViewType.RightView}
        onTargetElementChanged={onTargetElementChanged}
        onWayPointChanged={onWayPointChanged}
        onRenderedWaypointsChanged={onRenderedWaypointsChanged}
        onPanoTransformChanged={onPanoTransformChanged}
        {...toolEvents}
      />
    );
  }
  return (
    <WalkModelsStaticScene
      mainModel={currentObject}
      overlayModel={overlayObject}
      highlightedElement={highlightedElement}
      sheetForElevation={sheetForElevation}
      onUserWalkedTo={onUserWalkedTo}
      placeholders={placeholders}
      shouldShowPlaceholders={shouldShowPlaceholders}
      shouldShowAnnotations={shouldShowAnnotations}
      isPickingToolActive={activeToolAndPicking.isPickingToolActive}
      annotations={annotations}
      measurements={measurements}
      onWayPointChanged={onWayPointChanged}
      clippingPlanes={clippingPlanes}
      renderOnDemand={renderOnDemand}
      viewType={viewType}
      onAllModelsBoxChanged={onAllModelsBoxChanged}
      onRenderedWaypointsChanged={onRenderedWaypointsChanged}
      {...toolEvents}
    />
  );
}
